import { useNavigate } from "react-router-dom";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CriarFornecedorContract } from "./contracts/criar-fornecedor.contract";
import { criarFornecedorSchema } from "./schema/criar-fornecedor.schema";
import { useState } from "react";
import useLoadingStore from "../../shared/store/loading.store";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { toastError } from "../../shared/services/errors/erros-handler";
import { fornecedorService } from "./service/fornecedor.service";
import { ICnpjData } from "../../shared/components/buscarCNPJ/buscar-cnpj";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PessoaJuridicaFormPart } from "../../shared/components/formulario/parts/PessoaJuridicaFormPart/pessoa-juridica.part";
import { PessoaFisicaFormPart } from "../../shared/components/formulario/parts/PessoaFisicaFormPart/pessoa-fisica.part";
import InputDescricao from "../../shared/components/formulario/input-descricao/inputDescricao";
import { EnderecoFormPart } from "../../shared/components/formulario/parts/EnderecoFormPart/enderecoform.part";
import { SupplierTypeEnum } from "../../shared/utils/enum/enum";

export function CriarFornecedor() {
  const methods = useForm<CriarFornecedorContract>({
    resolver: yupResolver(criarFornecedorSchema),
    defaultValues: {
      type: SupplierTypeEnum.PJ,
      active: true,
      cnpj: null,
      cpf: null,
    },
  });

  const {
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = methods;
  const navigate = useNavigate();
  const [valueOpcao, setOpicao] = useState<"cpf" | "cnpj">("cnpj");
  const { setLoading } = useLoadingStore();

  const { mutate } = useMutation({
    mutationFn: async (data: CriarFornecedorContract) => {
      setLoading(true, "Estamos quase lá! Só mais um momento...");
      await fornecedorService.criar(data);
    },
    onSuccess: () => {
      toast.success("Fornecedor adicionado com sucesso");
      navigate("/fornecedores", { state: { reload: true } });
    },
    onError: (err: any) => {
      toastError(err);
    },
  });

  const preencherCampos = (dados: ICnpjData) => {
    setValue("corporateName", dados.razaoSocial || "");
    setValue("fantasyName", dados.fantasia || dados.razaoSocial || "");
  };

  const handleChangeOpcao = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOpicao(event.target.value as "cpf" | "cnpj");
  };

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));
  const tipoFornecedor = watch("type");

  return (
    <BaseLayout
      title="Adicionar Fornecedores"
      barraDeFerramentas={
        <FerramanetasDetalhe
          mostraBotaoApagar={false}
          mostraBotaoNovo={false}
          mostraBotaoSalvarEVoltar={false}
          mostraBotaoVoltar
          aoClicarBotaoSalvar={handleSubmit((formData) => {
            mutate(formData);
          })}
          aoClicarBotaoVoltar={() => navigate("/fornecedores")}
        />
      }
    >
      <Box
        component="form"
        onSubmit={handleSubmit((formData) => {
          mutate(formData);
        })}
        padding="20px 0"
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px)"
        gap="20px"
      >
        <Box
          display="grid"
          gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
          gap="20px"
          width="100%"
          flexWrap="wrap"
        >
          {/* Campo tipo do fornecedor */}
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <FormControl>
                <FormLabel>Tipo do Fornecedor</FormLabel>
                <RadioGroup {...field} row>
                  <FormControlLabel
                    value={SupplierTypeEnum.PJ}
                    control={<Radio />}
                    label="PJ"
                  />
                  <FormControlLabel
                    value={SupplierTypeEnum.MEI}
                    control={<Radio />}
                    label="MEI"
                  />
                  <FormControlLabel
                    value={SupplierTypeEnum.PF}
                    control={<Radio />}
                    label="PF"
                  />
                </RadioGroup>
              </FormControl>
            )}
          />

          {/* Exibe campos conforme o tipo selecionado */}
          {tipoFornecedor === SupplierTypeEnum.PJ ||
          tipoFornecedor === SupplierTypeEnum.MEI ? (
            <PessoaJuridicaFormPart
              methods={methods}
              preencherCampos={preencherCampos}
            />
          ) : (
            <PessoaFisicaFormPart methods={methods} mdDown={mdDown} />
          )}
        </Box>

        {/* Campos adicionais do schema */}
        <Box
          display="grid"
          gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
          gap="20px"
        >
          <Controller
            name="stateRegistration"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Inscrição Estadual"
                error={!!errors.stateRegistration}
                helperText={errors.stateRegistration?.message}
                fullWidth
                size="small"
              />
            )}
          />
          <Controller
            name="municipalRegistration"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Inscrição Municipal"
                error={!!errors.municipalRegistration}
                helperText={errors.municipalRegistration?.message}
                fullWidth
                size="small"
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Telefone"
                error={!!errors.phone}
                helperText={errors.phone?.message}
                fullWidth
                size="small"
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="E-mail"
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
                size="small"
              />
            )}
          />
          <Controller
            name="contact"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Contato"
                error={!!errors.contact}
                helperText={errors.contact?.message}
                fullWidth
                size="small"
              />
            )}
          />
          <Box sx={{ gridColumn: mdDown ? "auto" : "1 / -1" }}>
            <InputDescricao
              name="notes"
              methods={methods}
              label="Observações"
            />
          </Box>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(_, checked) => field.onChange(checked)}
                    />
                  }
                  label="Fornecedor Ativo"
                />
              </FormGroup>
            )}
          />
        </Box>

        {/* Endereço */}
        <EnderecoFormPart methods={methods} mdDown={mdDown} />
      </Box>
    </BaseLayout>
  );
}
